import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Empty, Input, InputNumber, Pagination, Select, Space, Spin, Table, Tabs, Tag, Typography, message } from 'antd';
import { ArrowLeftOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import {
    getGlobalProductsCoverageAPI,
    postGlobalProductsCoverageAPI,
    putGlobalProductsCoverageAPI,
    getOrganizationManagementProductsAPI
} from '../store/user/restCallUser';

type Row = Record<string, any>;

interface Props {
    organizationId: string;
    businessName: string;
    agreements: Row[];
    products: Row[];
    services: Row[];
    onBack: () => void;
}

const valueOf = (row: Row, ...names: string[]) => {
    for (const name of names) {
        if (row?.[name] !== undefined && row?.[name] !== null) return row[name];
    }
    return undefined;
};

const idOf = (row: Row) => String(valueOf(row, 'id', 'Id') || '');
const serviceIdOf = (row: Row) => String(valueOf(row, 'serviceId', 'ServiceId') || '');
const rowsOf = (response: any): Row[] => {
    let value = response?.Response ?? response?.data ?? response;
    for (let attempt = 0; attempt < 3; attempt += 1) {
        if (typeof value === 'string') {
            try { value = JSON.parse(value); continue; } catch { return []; }
        }
        if (value && !Array.isArray(value) && typeof value === 'object') {
            if (value.data !== undefined) { value = value.data; continue; }
            if (value.Response !== undefined) { value = value.Response; continue; }
        }
        break;
    }
    return Array.isArray(value) ? value : [];
};
const responseError = (response: any): string | null => {
    if (!response || response.Error !== null || response.StatusCode < 200 || response.StatusCode >= 300) {
        return response?.Error?.message || response?.Error || 'No se pudo completar la operación.';
    }
    return null;
};
const pageOf = (response: any): { items: Row[]; total: number; page: number } => {
    let value = response?.Response ?? response?.data ?? response;
    for (let attempt = 0; attempt < 3; attempt += 1) {
        if (typeof value === 'string') {
            try { value = JSON.parse(value); continue; } catch { return { items: [], total: 0, page: 1 }; }
        }
        if (value && !Array.isArray(value) && typeof value === 'object') {
            if (value.data !== undefined) { value = value.data; continue; }
            if (value.Response !== undefined) { value = value.Response; continue; }
        }
        break;
    }
    if (Array.isArray(value)) return { items: value, total: value.length, page: 1 };
    const items = Array.isArray(value?.items) ? value.items : Array.isArray(value?.Items) ? value.Items : [];
    return {
        items,
        total: Number(value?.total ?? value?.Total ?? items.length),
        page: Number(value?.page ?? value?.Page ?? 1)
    };
};

export default function AgreementPriceMatrix({
    organizationId,
    businessName,
    agreements,
    products,
    services,
    onBack
}: Props) {
    const [coverageByAgreement, setCoverageByAgreement] = useState<Record<string, Row[]>>({});
    const [drafts, setDrafts] = useState<Record<string, number | null>>({});
    const [serviceId, setServiceId] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [matrixProducts, setMatrixProducts] = useState<Row[]>(products);
    const [productTotal, setProductTotal] = useState(products.length);
    const [productLoading, setProductLoading] = useState(false);
    const pageSize = 25;
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const visibleAgreements = useMemo(
        () => agreements.filter(agreement => !['CANCELADO', 'VENCIDO'].includes(
            String(valueOf(agreement, 'status', 'Status') || '').toUpperCase()
        )),
        [agreements]
    );

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const entries = await Promise.all(visibleAgreements.map(async agreement => {
                const agreementId = idOf(agreement);
                const response = await getGlobalProductsCoverageAPI(
                    organizationId,
                    agreementId,
                    serviceId || undefined,
                    search,
                    page,
                    pageSize
                );
                const failure = responseError(response);
                if (failure) throw new Error(`${valueOf(agreement, 'name', 'Name') || agreementId}: ${failure}`);
                return [agreementId, pageOf(response).items] as [string, Row[]];
            }));
            setCoverageByAgreement(Object.fromEntries(entries));
            setDrafts({});
        } catch (error: any) {
            message.error(error?.message || 'No se pudo cargar la matriz de precios.');
        } finally {
            setLoading(false);
        }
    }, [organizationId, page, pageSize, search, serviceId, visibleAgreements]);

    useEffect(() => { void load(); }, [load]);

    useEffect(() => {
        setMatrixProducts(products);
        setProductTotal(products.length);
    }, [products]);

    const loadProductsPage = useCallback(async () => {
        setProductLoading(true);
        try {
            const response = await getOrganizationManagementProductsAPI(
                organizationId,
                search.trim() || undefined,
                false,
                serviceId || undefined,
                page,
                pageSize
            );
            const failure = responseError(response);
            if (failure) throw new Error(String(failure));
            const result = pageOf(response);
            setMatrixProducts(result.items);
            setProductTotal(result.total);
        } catch (error: any) {
            // Mantiene operativa la matriz mientras una instalación antigua
            // del gateway aún no soporte page/pageSize.
            const normalizedSearch = search.trim().toLowerCase();
            const fallback = products
                .filter(product => valueOf(product, 'organizationProductId', 'OrganizationProductId'))
                .filter(product => !serviceId || serviceIdOf(product) === serviceId)
                .filter(product => !normalizedSearch || [
                    valueOf(product, 'code', 'Code'),
                    valueOf(product, 'description', 'Description'),
                    valueOf(product, 'commercialName', 'CommercialName'),
                    valueOf(product, 'organizationCommercialName', 'OrganizationCommercialName')
                ].some(value => String(value || '').toLowerCase().includes(normalizedSearch)));
            setProductTotal(fallback.length);
            setMatrixProducts(fallback.slice((page - 1) * pageSize, page * pageSize));
            message.warning(error?.message || 'No se pudo cargar la página de productos; se usó la lista disponible.');
        } finally {
            setProductLoading(false);
        }
    }, [organizationId, page, pageSize, products, search, serviceId]);

    useEffect(() => { void loadProductsPage(); }, [loadProductsPage]);

    const productRows = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        return matrixProducts
            .filter(product => valueOf(product, 'organizationProductId', 'OrganizationProductId'))
            .filter(product => !serviceId || String(valueOf(product, 'serviceId', 'ServiceId')) === serviceId)
            .filter(product => {
                if (!normalizedSearch) return true;
                return [
                    valueOf(product, 'code', 'Code'),
                    valueOf(product, 'description', 'Description'),
                    valueOf(product, 'commercialName', 'CommercialName'),
                    valueOf(product, 'organizationCommercialName', 'OrganizationCommercialName')
                ].some(value => String(value || '').toLowerCase().includes(normalizedSearch));
            })
            .sort((left, right) => String(valueOf(left, 'description', 'Description') || '')
                .localeCompare(String(valueOf(right, 'description', 'Description') || '')));
    }, [matrixProducts, search, serviceId]);

    // La matriz se divide por servicio y conserva la paginación al cambiar
    // de convenio. Los borradores viven en `drafts`, por lo que cambiar de
    // servicio o de página no elimina los precios que todavía no se guardan.
    const serviceTabs = useMemo(() => [{
        key: '__all__',
            label: `Todos (${products.filter(product => valueOf(product, 'organizationProductId', 'OrganizationProductId')).length})`
    }, ...services
        .filter(service => serviceIdOf(service))
        .map(service => {
            const id = serviceIdOf(service);
            const count = products.filter(product =>
                valueOf(product, 'organizationProductId', 'OrganizationProductId')
                && String(valueOf(product, 'serviceId', 'ServiceId') || '') === id
            ).length;
            return {
                key: id,
                label: `${valueOf(service, 'name', 'Name') || id} (${count})`
            };
        })], [products, services]);

    const coverageFor = (agreementId: string, product: Row) => {
        const productId = idOf(product);
        const organizationProductId = String(valueOf(product, 'organizationProductId', 'OrganizationProductId') || '');
        return (coverageByAgreement[agreementId] || []).find(item =>
            String(valueOf(item, 'productId', 'ProductId') || '') === productId
            || String(valueOf(item, 'organizationProductId', 'OrganizationProductId') || '') === organizationProductId
        );
    };

    const draftKey = (agreementId: string, product: Row) =>
        `${agreementId}:${String(valueOf(product, 'organizationProductId', 'OrganizationProductId') || idOf(product))}`;

    const save = async () => {
        const pending = Object.entries(drafts).filter(([, price]) => price !== null && price !== undefined);
        if (!pending.length) {
            message.info('No hay cambios de precios pendientes.');
            return;
        }

        setSaving(true);
        const failures: string[] = [];
        const completedKeys: string[] = [];
        try {
            for (const [key, price] of pending) {
                const separator = key.indexOf(':');
                const agreementId = key.slice(0, separator);
                const organizationProductId = key.slice(separator + 1);
                const agreement = visibleAgreements.find(item => idOf(item) === agreementId);
                const product = productRows.find(item =>
                    String(valueOf(item, 'organizationProductId', 'OrganizationProductId') || idOf(item)) === organizationProductId
                ) || products.find(item =>
                    String(valueOf(item, 'organizationProductId', 'OrganizationProductId') || idOf(item)) === organizationProductId
                );
                if (!product || !agreement) {
                    failures.push(`No se encontró el producto o convenio de la celda ${key}.`);
                    continue;
                }

                const existing = coverageFor(agreementId, product);
                const payload = {
                    organizationProductId,
                    price: Number(price),
                    description: valueOf(existing || {}, 'description', 'Description') || 'Tarifa global del convenio'
                };
                const response = existing
                    ? await putGlobalProductsCoverageAPI(organizationId, agreementId, String(valueOf(existing, 'id', 'Id')), payload)
                    : await postGlobalProductsCoverageAPI(organizationId, agreementId, payload);
                const failure = responseError(response);
                if (failure) {
                    failures.push(`${valueOf(agreement, 'name', 'Name') || agreementId}: ${failure}`);
                    continue;
                }
                setCoverageByAgreement(previous => ({ ...previous, [agreementId]: rowsOf(response) }));
                completedKeys.push(key);
            }
            if (completedKeys.length) {
                setDrafts(previous => {
                    const next = { ...previous };
                    completedKeys.forEach(key => delete next[key]);
                    return next;
                });
            }
            if (failures.length) {
                message.warning(`Se guardaron ${completedKeys.length} celdas; ${failures.length} requieren revisión.`);
            } else {
                message.success(`${completedKeys.length} precios guardados correctamente.`);
            }
        } finally {
            setSaving(false);
        }
    };

    const columns: any[] = [
        {
            title: 'Producto',
            fixed: 'left',
            width: 300,
            render: (_: unknown, product: Row) => <Space direction="vertical" size={0}>
                <Typography.Text strong>{valueOf(product, 'organizationCommercialName', 'OrganizationCommercialName', 'commercialName', 'CommercialName') || valueOf(product, 'description', 'Description')}</Typography.Text>
                <Typography.Text type="secondary">{valueOf(product, 'code', 'Code')} · {valueOf(product, 'description', 'Description')}</Typography.Text>
            </Space>
        },
        {
            title: 'Servicio',
            width: 180,
            render: (_: unknown, product: Row) => {
                const service = services.find(item => String(valueOf(item, 'serviceId', 'ServiceId')) === String(valueOf(product, 'serviceId', 'ServiceId')));
                return valueOf(service || {}, 'name', 'Name') || valueOf(product, 'serviceName', 'ServiceName') || 'Sin servicio';
            }
        },
        ...visibleAgreements.map(agreement => {
            const agreementId = idOf(agreement);
            return {
                title: <Space direction="vertical" size={0}>
                    <Typography.Text strong>{valueOf(agreement, 'name', 'Name') || 'Convenio'}</Typography.Text>
                    <Tag color={String(valueOf(agreement, 'status', 'Status') || '').toUpperCase() === 'ACTIVO' ? 'green' : 'default'}>{valueOf(agreement, 'status', 'Status')}</Tag>
                </Space>,
                width: 170,
                render: (_: unknown, product: Row) => {
                    const coverage = coverageFor(agreementId, product);
                    const key = draftKey(agreementId, product);
                    const draft = drafts[key];
                    const value = draft !== undefined ? draft : (coverage ? Number(valueOf(coverage, 'price', 'Price')) : null);
                    return <InputNumber
                        min={0}
                        precision={2}
                        value={value}
                        placeholder={coverage ? undefined : 'Sin tarifa'}
                        style={{ width: '100%' }}
                        onChange={next => setDrafts(previous => ({ ...previous, [key]: next }))}
                    />;
                }
            };
        })
    ];

    return <Card
        title={`Matriz de precios · ${businessName}`}
        extra={<Space><Button icon={<ArrowLeftOutlined />} onClick={onBack}>Volver a convenios</Button><Button icon={<ReloadOutlined />} onClick={() => { void load(); void loadProductsPage(); }}>Actualizar</Button><Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => void save()}>Guardar cambios</Button></Space>}
    >
        <Alert
            type="info"
            showIcon
            message="Tarifas por convenio"
            description="Las filas son productos ya registrados en la organización y las columnas son los convenios de la aseguradora o tercero. Una celda vacía significa que todavía no existe una tarifa global; la cobertura y el copago se completan después desde la gestión del convenio."
            style={{ marginBottom: 16 }}
        />
        <Space wrap style={{ marginBottom: 16 }}>
            <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Filtrar por servicio clínico"
                style={{ width: 280 }}
                value={serviceId || undefined}
                onChange={value => { setServiceId(value || ''); setPage(1); }}
                options={services.map(service => ({
                    value: String(valueOf(service, 'serviceId', 'ServiceId') || ''),
                    label: String(valueOf(service, 'name', 'Name') || '')
                }))}
            />
            <Input.Search
                allowClear
                placeholder="Buscar por código o nombre"
                style={{ width: 300 }}
                value={search}
                onChange={event => { setSearch(event.target.value); setPage(1); }}
                onSearch={value => { setSearch(value); setPage(1); }}
            />
            <Typography.Text type="secondary">{productTotal} productos · {visibleAgreements.length} convenios</Typography.Text>
        </Space>
        {!loading && visibleAgreements.length > 0 && productRows.length > 0 && <Tabs
            activeKey={serviceId || '__all__'}
            onChange={key => { setServiceId(key === '__all__' ? '' : key); setPage(1); }}
            items={serviceTabs}
            style={{ marginBottom: 8 }}
        />}
        {loading || productLoading ? <Spin /> : !visibleAgreements.length ? <Empty description="No hay convenios activos o en borrador" /> : !productTotal ? <Empty description="No hay productos registrados para este filtro" /> : <Table
            rowKey={row => String(valueOf(row, 'organizationProductId', 'OrganizationProductId') || idOf(row))}
            size="small"
            bordered
            pagination={false}
            scroll={{ x: 650 + (visibleAgreements.length * 170) }}
            dataSource={productRows}
            columns={columns}
        />}
        {!loading && !productLoading && visibleAgreements.length > 0 && productTotal > 0 && <Pagination
            current={page}
            pageSize={pageSize}
            total={productTotal}
            showSizeChanger={false}
            showTotal={total => `${total} productos del servicio seleccionado`}
            onChange={nextPage => setPage(nextPage)}
            style={{ marginTop: 16, textAlign: 'right' }}
        />}
    </Card>;
}
